export const SITE_NAME = 'Pricedrop';
export const SITE_DOMAIN = 'pricedrop.co.il';
export const DEFAULT_OG_IMAGE = '/og-default.png';
export const OG_LOCALE = 'he_IL';

/**
 * Build an absolute canonical URL for the production domain.
 * Always uses https://pricedrop.co.il — independent of environment.
 * Strips trailing slashes (except root "/").
 */
export function buildCanonicalUrl(path: string): string {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const cleanPath =
    normalizedPath.length > 1 && normalizedPath.endsWith('/')
      ? normalizedPath.slice(0, -1)
      : normalizedPath;
  return `https://${SITE_DOMAIN}${cleanPath}`;
}

export interface SeoOptions {
  title: string;
  description: string;
  canonicalUrl: string;
  ogImage?: string;
  ogType?: string;
  noindex?: boolean;
}

type MetaTag =
  | { title: string }
  | { name: string; content: string }
  | { property: string; content: string };

/**
 * Build the complete meta tag array for a route's head() callback.
 *
 * For noindex pages (auth/protected): returns only title + robots (no OG/Twitter tags).
 * For public pages: returns title + description + full OG + Twitter tags.
 *
 * IMPORTANT: Never call this from __root.tsx — only from individual child routes.
 * OG tags at root level bleed through to noindex pages (TanStack Start head merging
 * only overrides by name/property, cannot delete parent tags).
 */
export function buildMeta(options: SeoOptions): MetaTag[] {
  const { title, description, canonicalUrl, ogImage, ogType = 'website', noindex } = options;

  if (noindex) {
    return [{ title }, { name: 'robots', content: 'noindex, nofollow' }];
  }

  const absoluteUrl = buildCanonicalUrl(canonicalUrl);
  const absoluteImage = !ogImage
    ? `https://${SITE_DOMAIN}${DEFAULT_OG_IMAGE}`
    : ogImage.startsWith('http')
      ? ogImage
      : `https://${SITE_DOMAIN}${ogImage.startsWith('/') ? '' : '/'}${ogImage}`;

  return [
    { title },
    { name: 'description', content: description },
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    { property: 'og:image', content: absoluteImage },
    { property: 'og:url', content: absoluteUrl },
    { property: 'og:type', content: ogType },
    { property: 'og:site_name', content: SITE_NAME },
    { property: 'og:locale', content: OG_LOCALE },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: description },
    { name: 'twitter:image', content: absoluteImage },
  ];
}

/**
 * Build a canonical link object for the links array in head().
 */
export function buildCanonicalLink(path: string): { rel: string; href: string } {
  return { rel: 'canonical', href: buildCanonicalUrl(path) };
}

/**
 * Build a JSON-LD script tag object for the head() scripts/headScripts array.
 *
 * Use in `scripts` array by default. Task 4 validates which property
 * (<HeadContent /> renders scripts or headScripts into <head>).
 */
export function buildJsonLd(data: object): { type: string; children: string } {
  return {
    type: 'application/ld+json',
    children: JSON.stringify(data),
  };
}
